import React from 'react';
import MonthlyCalendar from 'Components/UI/Calendar/Monthly';
import DateNavigator from 'Components/UI/Calendar/DateNavigator';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import useMobileLayout from 'Hooks/useMobileLayout';
import MobileCalendarLegend from 'Components/Modals/ClientSchedulerModal/Default/MonthlyCalendar/MobileCalendarLegend';
import {
  fetchDataFromLinkQueryParam,
  setNewLinkQueryParam,
  useClientSchedulerDispatchContext,
  useClientSchedulerStateContext,
} from 'Context/NewClientSchedulerContext';
import moment from 'moment';
import { fullDateISO } from 'Consts/DateFormats';
import { ArrowRight, ArrowLeft } from '@material-ui/icons';
import MobileSchedulerDayWithWeekends
  from 'Components/Modals/ClientSchedulerModal/Default/MonthlyCalendar/MobileSchedulerDayWithWeekends';
import DesktopSchedulerDayWithWeekends
  from 'Components/Modals/ClientSchedulerModal/Default/MonthlyCalendar/DesktopSchedulerDayWithWeekends';

const styles = theme => ({
  mobileDateNavigator: {
    justifyContent: 'center',
  },
  mobileRoot: {
    flexGrow: 0,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  mobileContent: {
    flexGrow: 0,
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: theme.dimensions.xxl,
    position: 'relative',
  },
  dateNav: {
    backgroundColor: 'unset',
  },
  dateNavContent: {
    color: theme.palette.facet.primaryBlue,
  },
  weekdayHeader: {
    backgroundColor: 'unset',
  },
  weekdayHeaderContent: {
    color: theme.palette.facet.primaryGray,
  },
});

const MonthCalendarContainer = ({
  classes,
}) => {
  const { currentCollection, loading } = useClientSchedulerStateContext();
  const dispatch = useClientSchedulerDispatchContext();
  const { logEvent } = useEventTrackingLogEvent();
  const isMobile = useMobileLayout();

  const availabilityPeriodResource = currentCollection.resources().head();
  const currentDate = moment(availabilityPeriodResource.attr('currentDate'));
  const prevDate = moment(availabilityPeriodResource.attr('prevDate'));
  const nextDate = moment(availabilityPeriodResource.attr('nextDate'));

  const availabilityDayItems = availabilityPeriodResource.attr('availabilityDays').resources().list().map(resource => ({
    resource,
    date: moment.utc(resource.attr('day')).format(fullDateISO),
  }));

  const mobileDateNavigatorStyles = isMobile ? {
    content: classes.mobileDateNavigator,
  } : null;

  const mobileMonthlyCalendarStyles = isMobile ? {
    root: classes.mobileRoot,
    content: classes.mobileContent,
  } : null;

  const DayComponent = isMobile ? MobileSchedulerDayWithWeekends : DesktopSchedulerDayWithWeekends;

  const handleDayNumberClick = (date) => {
    logEvent('Clicked Date from Client Scheduler Monthly View', {
      selectedDate: date,
      mobileView: isMobile,
    });
  };

  const goToPrevMonth = () => {
    setNewLinkQueryParam(dispatch, availabilityPeriodResource.link('prev').url());
    fetchDataFromLinkQueryParam(dispatch);
  };

  const goToNextMonth = () => {
    setNewLinkQueryParam(dispatch, availabilityPeriodResource.link('next').url());
    fetchDataFromLinkQueryParam(dispatch);
  };

  return (
    <>
      <DateNavigator
        date={currentDate}
        prevDate={prevDate}
        nextDate={nextDate}
        goToPrevDate={goToPrevMonth}
        goToNextDate={goToNextMonth}
        isLoading={loading}
        classes={mobileDateNavigatorStyles}
        goToNextDateEventName="Clicked Next Month From Client Scheduler"
        goToPrevDateEventName="Clicked Previous Month From Client Scheduler"
        goToNextDateEventProperties={{
          mobileView: isMobile,
        }}
        goToPrevDateEventProperties={{
          mobileView: isMobile,
        }}
        className={classes.dateNav}
        contentClassName={classes.dateNavContent}
        BackIconButtonProps={{
          color: 'primary',
        }}
        PrevIconButtonProps={{
          icon: ArrowLeft,
          color: 'primary',
        }}
        NextIconButtonProps={{
          icon: ArrowRight,
          color: 'primary',
        }}
        MonthLabelProps={{
          weight: 'medium',
        }}
      />
      <MonthlyCalendar
        date={currentDate}
        items={availabilityDayItems}
        onDayNumberClick={handleDayNumberClick}
        DayComponent={DayComponent}
        classes={mobileMonthlyCalendarStyles}
        WeekdaysHeaderProps={{
          className: classes.weekdayHeader,
          contentClassName: classes.weekdayHeaderContent,
        }}
      />
      {isMobile && <MobileCalendarLegend />}
    </>
  );
};

MonthCalendarContainer.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(MonthCalendarContainer);
